import React, { memo } from 'react'

import {
  FaFile,
  FaFileCode,
  FaFileExcel,
  FaFileImage,
  FaFileLines,
  FaFilePdf,
  FaFilePen,
  FaFilePowerpoint,
  FaFileWord,
  FaFileZipper,
  FaJava,
} from 'react-icons/fa6'
import {
  HiOutlineComputerDesktop,
  HiOutlineDocument,
  HiOutlineDocumentText,
} from 'react-icons/hi2'
import {
  PiFile,
  PiFileImage,
  PiFilePdf,
  PiFileText,
  PiFileZip,
  PiMicrosoftExcelLogo,
  PiMicrosoftPowerpointLogo,
  PiMicrosoftWordLogo,
} from 'react-icons/pi'
import {
  SiC,
  SiCplusplus,
  SiDart,
  SiGnubash,
  SiGo,
  SiJavascript,
  SiJson,
  SiKotlin,
  SiLua,
  SiPhp,
  SiPython,
  SiR,
  SiRuby,
  SiRust,
  SiSharp,
  SiSwift,
  SiToml,
  SiTypescript,
  SiYaml,
} from 'react-icons/si'
import { twMerge } from 'tailwind-merge'

import { getFileExtensionFromName } from '@/helpers/file'

export type ThumbnailType =
  // file extensions
  | 'csv'
  | 'doc'
  | 'docx'
  | 'dotx'
  | 'gif'
  | 'jpg'
  | 'jpeg'
  | 'png'
  | 'webp'
  | 'odt'
  | 'pdf'
  | 'pptx'
  | 'rtf'
  | 'tex'
  | 'text'
  | 'txt'
  | 'xls'
  | 'xlsx'
  | 'zip'
  // misc
  | 'local'

interface Thumbnail {
  type: ThumbnailType | string
  className?: string
}

interface ThumbnailContainer {
  children: React.ReactNode
  className?: string
}

export const ThumbnailContainer: React.FC<ThumbnailContainer> = ({
  children,
  className,
}) => {
  return (
    <div
      className={twMerge(
        'flex items-center justify-center p-2 rounded-[5px]',
        className,
      )}
    >
      {children}
    </div>
  )
}

export const Thumbnail: React.FC<Thumbnail> = ({ type, className }) => {
  switch (type) {
    case 'csv':
      return (
        <ThumbnailContainer
          className={twMerge('bg-green-600 dark:bg-green-600', className)}
        >
          <PiMicrosoftExcelLogo className="w-4 h-4 text-white dark:text-white" />
        </ThumbnailContainer>
      )
    case 'doc':
      return (
        <ThumbnailContainer
          className={twMerge('bg-blue-600 dark:bg-blue-600', className)}
        >
          <PiMicrosoftWordLogo className="w-4 h-4 text-white dark:text-white" />
        </ThumbnailContainer>
      )
    case 'docx':
      return (
        <ThumbnailContainer
          className={twMerge('bg-blue-600 dark:bg-blue-600', className)}
        >
          <PiMicrosoftWordLogo className="w-4 h-4 text-white dark:text-white" />
        </ThumbnailContainer>
      )
    case 'dotx':
      return (
        <ThumbnailContainer
          className={twMerge('bg-blue-600 dark:bg-blue-600', className)}
        >
          <PiMicrosoftWordLogo className="w-4 h-4 text-white dark:text-white" />
        </ThumbnailContainer>
      )
    case 'gif':
      return (
        <ThumbnailContainer
          className={twMerge('bg-primary-200 dark:bg-primary-700', className)}
        >
          <PiFileImage className="w-4 h-4 text-primary-900 dark:text-primary-100" />
        </ThumbnailContainer>
      )
    case 'jpg':
      return (
        <ThumbnailContainer
          className={twMerge('bg-primary-200 dark:bg-primary-700', className)}
        >
          <PiFileImage className="w-4 h-4 text-primary-900 dark:text-primary-100" />
        </ThumbnailContainer>
      )
    case 'jpeg':
      return (
        <ThumbnailContainer
          className={twMerge('bg-primary-200 dark:bg-primary-700', className)}
        >
          <PiFileImage className="w-4 h-4 text-primary-900 dark:text-primary-100" />
        </ThumbnailContainer>
      )
    case 'png':
      return (
        <ThumbnailContainer
          className={twMerge('bg-primary-200 dark:bg-primary-700', className)}
        >
          <PiFileImage className="w-4 h-4 text-primary-900 dark:text-primary-100" />
        </ThumbnailContainer>
      )
    case 'webp':
      return (
        <ThumbnailContainer
          className={twMerge('bg-primary-200 dark:bg-primary-700', className)}
        >
          <PiFileImage className="w-4 h-4 text-primary-900 dark:text-primary-100" />
        </ThumbnailContainer>
      )
    case 'odt':
      return (
        <ThumbnailContainer
          className={twMerge('bg-primary-200 dark:bg-primary-700', className)}
        >
          <PiFileText className="w-4 h-4 text-primary-900 dark:text-primary-100" />
        </ThumbnailContainer>
      )
    case 'pdf':
      return (
        <ThumbnailContainer
          className={twMerge('bg-red-500 dark:bg-red-500', className)}
        >
          <PiFilePdf className="w-4 h-4 text-white dark:text-white" />
        </ThumbnailContainer>
      )
    case 'pptx':
      return (
        <ThumbnailContainer
          className={twMerge('bg-orange-600 dark:bg-orange-500', className)}
        >
          <PiMicrosoftPowerpointLogo className="w-4 h-4 text-white dark:text-white" />
        </ThumbnailContainer>
      )
    case 'rtf':
      return (
        <ThumbnailContainer
          className={twMerge('bg-primary-200 dark:bg-primary-700', className)}
        >
          <PiFileText className="w-4 h-4 text-primary-900 dark:text-primary-100" />
        </ThumbnailContainer>
      )
    case 'tex':
      return (
        <ThumbnailContainer
          className={twMerge('bg-primary-200 dark:bg-primary-700', className)}
        >
          <PiFileText className="w-4 h-4 text-primary-900 dark:text-primary-100" />
        </ThumbnailContainer>
      )
    case 'text':
      return (
        <ThumbnailContainer
          className={twMerge('bg-primary-200 dark:bg-primary-700', className)}
        >
          <PiFileText className="w-4 h-4 text-primary-900 dark:text-primary-100" />
        </ThumbnailContainer>
      )
    case 'txt':
      return (
        <ThumbnailContainer
          className={twMerge('bg-primary-200 dark:bg-primary-700', className)}
        >
          <PiFileText className="w-4 h-4 text-primary-900 dark:text-primary-100" />
        </ThumbnailContainer>
      )
    case 'xls':
      return (
        <ThumbnailContainer
          className={twMerge('bg-green-600 dark:bg-green-600', className)}
        >
          <PiMicrosoftExcelLogo className="w-4 h-4 text-white dark:text-white" />
        </ThumbnailContainer>
      )
    case 'xlsx':
      return (
        <ThumbnailContainer
          className={twMerge('bg-green-600 dark:bg-green-600', className)}
        >
          <PiMicrosoftExcelLogo className="w-4 h-4 text-white dark:text-white" />
        </ThumbnailContainer>
      )
    case 'zip':
      return (
        <ThumbnailContainer
          className={twMerge('bg-primary-200 dark:bg-primary-700', className)}
        >
          <PiFileZip className="w-4 h-4 text-primary-900 dark:text-primary-100" />
        </ThumbnailContainer>
      )
    case 'local':
      return (
        <ThumbnailContainer
          className={twMerge('bg-primary-200 dark:bg-primary-700', className)}
        >
          <HiOutlineComputerDesktop className="w-4 h-4 text-primary-900 dark:text-primary-100" />
        </ThumbnailContainer>
      )
    case 'draft':
      return (
        <ThumbnailContainer
          className={twMerge('bg-primary-200 dark:bg-primary-700', className)}
        >
          <HiOutlineDocument className="w-4 h-4 text-primary-900 dark:text-primary-100" />
        </ThumbnailContainer>
      )
    case 'document':
      return (
        <ThumbnailContainer
          className={twMerge('bg-accent-400 dark:bg-accent-400', className)}
        >
          <HiOutlineDocumentText className="w-4 h-4 text-white dark:text-white" />
        </ThumbnailContainer>
      )
    default:
      return (
        <ThumbnailContainer
          className={twMerge('bg-primary-200 dark:bg-primary-700', className)}
        >
          <PiFile className="w-4 h-4 text-primary-900 dark:text-primary-100" />
        </ThumbnailContainer>
      )
  }
}

interface CompactThumbnailProps {
  name: string
  className?: string
  size?: number
}

interface CompactThumbnailVisual {
  Icon: React.ComponentType<{ size?: number; className?: string }>
  colorClassName: string
}

const compactThumbnailVisuals = (
  Icon: CompactThumbnailVisual['Icon'],
  colorClassName: string,
  extensions: string[],
): Array<[string, CompactThumbnailVisual]> =>
  extensions.map((extension) => [extension, { Icon, colorClassName }])

const COMPACT_THUMBNAIL_VISUALS: Record<string, CompactThumbnailVisual> =
  Object.fromEntries([
    ...compactThumbnailVisuals(
      FaFileExcel,
      'text-green-600 dark:text-green-500',
      ['csv', 'tsv', 'xls', 'xlsx', 'xlsm', 'xltx', 'xltm'],
    ),
    ...compactThumbnailVisuals(FaFileWord, 'text-blue-600 dark:text-blue-500', [
      'doc',
      'docx',
      'dotx',
      'odt',
    ]),
    ...compactThumbnailVisuals(
      FaFilePowerpoint,
      'text-red-600 dark:text-red-400',
      ['ppt', 'pptx'],
    ),
    ...compactThumbnailVisuals(FaFilePdf, 'text-red-600 dark:text-red-500', [
      'pdf',
    ]),
    ...compactThumbnailVisuals(FaFilePen, 'text-sky-600 dark:text-sky-400', [
      'md',
      'markdown',
    ]),
    ...compactThumbnailVisuals(
      FaFileImage,
      'text-green-600 dark:text-green-400',
      ['gif', 'jpg', 'jpeg', 'png', 'webp', 'svg', 'bmp', 'tiff', 'ico'],
    ),
    ...compactThumbnailVisuals(
      FaFileLines,
      'text-purple-600 dark:text-purple-400',
      ['rtf', 'tex', 'text', 'txt', 'log'],
    ),
    ...compactThumbnailVisuals(
      FaFileZipper,
      'text-blue-600 dark:text-blue-400',
      ['zip', 'tar', 'gz', '7z', 'rar'],
    ),
    // Source code and structured data/config formats. Brand glyphs for the
    // major languages, each tinted with the language's conventional color
    // (same palette as getFileTypeIcon); config formats stay neutral.
    ...compactThumbnailVisuals(
      FaFileCode,
      'text-orange-600 dark:text-orange-400',
      ['htm', 'html'],
    ),
    ...compactThumbnailVisuals(SiPython, 'text-blue-600 dark:text-blue-400', [
      'py',
    ]),
    ...compactThumbnailVisuals(
      SiJavascript,
      'text-amber-500 dark:text-amber-400',
      ['js', 'mjs', 'cjs', 'jsx'],
    ),
    ...compactThumbnailVisuals(
      SiTypescript,
      'text-blue-600 dark:text-blue-400',
      ['ts', 'tsx'],
    ),
    ...compactThumbnailVisuals(FaJava, 'text-red-600 dark:text-red-400', [
      'java',
    ]),
    ...compactThumbnailVisuals(SiC, 'text-blue-600 dark:text-blue-400', [
      'c',
      'h',
    ]),
    ...compactThumbnailVisuals(SiCplusplus, 'text-sky-600 dark:text-sky-400', [
      'cpp',
      'hpp',
      'cc',
    ]),
    ...compactThumbnailVisuals(
      SiSharp,
      'text-violet-600 dark:text-violet-400',
      ['cs'],
    ),
    ...compactThumbnailVisuals(SiGo, 'text-cyan-600 dark:text-cyan-400', [
      'go',
    ]),
    ...compactThumbnailVisuals(SiRust, 'text-orange-600 dark:text-orange-400', [
      'rs',
    ]),
    ...compactThumbnailVisuals(SiRuby, 'text-red-600 dark:text-red-400', [
      'rb',
    ]),
    ...compactThumbnailVisuals(SiPhp, 'text-indigo-600 dark:text-indigo-400', [
      'php',
    ]),
    ...compactThumbnailVisuals(
      SiSwift,
      'text-orange-600 dark:text-orange-400',
      ['swift'],
    ),
    ...compactThumbnailVisuals(
      SiKotlin,
      'text-purple-600 dark:text-purple-400',
      ['kt'],
    ),
    ...compactThumbnailVisuals(
      SiGnubash,
      'text-emerald-600 dark:text-emerald-400',
      ['sh', 'bash', 'zsh'],
    ),
    ...compactThumbnailVisuals(SiR, 'text-blue-600 dark:text-blue-400', ['r']),
    ...compactThumbnailVisuals(SiLua, 'text-blue-600 dark:text-blue-400', [
      'lua',
    ]),
    ...compactThumbnailVisuals(SiDart, 'text-sky-600 dark:text-sky-400', [
      'dart',
    ]),
    ...compactThumbnailVisuals(SiJson, 'text-amber-500 dark:text-amber-400', [
      'json',
      'jsonl',
      'ndjson',
    ]),
    ...compactThumbnailVisuals(SiYaml, 'text-gray-500 dark:text-gray-400', [
      'yaml',
      'yml',
    ]),
    ...compactThumbnailVisuals(SiToml, 'text-gray-500 dark:text-gray-400', [
      'toml',
    ]),
    ...compactThumbnailVisuals(FaFileCode, 'text-teal-600 dark:text-teal-400', [
      'sql',
    ]),
    ...compactThumbnailVisuals(FaFileCode, 'text-sky-600 dark:text-sky-400', [
      'css',
      'scss',
      'less',
    ]),
    ...compactThumbnailVisuals(FaFileCode, 'text-gray-500 dark:text-gray-400', [
      'xml',
      'ini',
      'cfg',
      'conf',
      'ps1',
    ]),
  ])

export const CompactThumbnail: React.FC<CompactThumbnailProps> = memo(
  ({ name, size = 18, className }) => {
    const extension = getFileExtensionFromName(name)
    const visual = COMPACT_THUMBNAIL_VISUALS[extension] ?? {
      Icon: FaFile,
      colorClassName: 'text-gray-500 dark:text-gray-400',
    }
    return (
      <visual.Icon
        size={size}
        className={twMerge('flex-shrink-0', visual.colorClassName, className)}
      />
    )
  },
  (prevProps, nextProps) => {
    return (
      prevProps.name === nextProps.name && prevProps.size === nextProps.size
    )
  },
)

CompactThumbnail.displayName = 'CompactThumbnail'

interface ThumbnailV2Props {
  name: string
  className?: string
  size?: number
  containerClassName?: string
}

export const ThumbnailV2: React.FC<ThumbnailV2Props> = memo(
  ({ name, size = 18, className, containerClassName }) => {
    return (
      <div
        className={twMerge(
          'w-9 h-9 bg-primary-100 dark:bg-primary-800 rounded-lg flex items-center justify-center flex-shrink-0 mt-1',
          containerClassName,
        )}
      >
        <CompactThumbnail name={name} size={size} className={className} />
      </div>
    )
  },
  (prevProps, nextProps) => {
    return (
      prevProps.name === nextProps.name && prevProps.size === nextProps.size
    )
  },
)

ThumbnailV2.displayName = 'ThumbnailV2'
